import { Box, Button, Flex, Grid, HStack, IconButton, Stack, Text } from "@chakra-ui/react";
import { Bell, BellRing, Menu, Radio, ShieldCheck } from "lucide-react";
import { useCustom, useList, useNotification } from "@refinedev/core";
import { useEffect, useRef, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { ThemeToggle } from "../components/ui";
import { ExecutionModePills, RuntimePulseDot } from "../components/runtime-activity";
import { navGroups } from "./nav";
import { ALERT_READ_CHANGED_EVENT } from "../providers/alert-sync";
import { API_REQUEST_FAILED_EVENT, type ApiRequestFailure } from "../providers/api-client";
import type { AlertRecord, AlertSummary, BotRecord } from "../types/domain";

const SHOWN_ALERTS_KEY = "trading:shown-alert-toasts:v2";

function RuntimeNotificationBridge() {
  const { open } = useNotification();
  const alerts = useList<AlertRecord>({
    resource: "alerts",
    pagination: { currentPage: 1, pageSize: 200 },
    liveMode: "auto",
    queryOptions: { refetchInterval: 5_000 },
  }).result.data ?? [];
  const shownAlerts = useRef<Set<string> | undefined>(undefined);
  const recentFailures = useRef(new Map<string, number>());

  if (!shownAlerts.current) {
    let restored: string[] = [];
    try { restored = JSON.parse(window.sessionStorage.getItem(SHOWN_ALERTS_KEY) ?? "[]") as string[]; } catch { restored = []; }
    shownAlerts.current = new Set(restored);
  }

  useEffect(() => {
    const pending = alerts
      .filter((alert) => !alert.read && !shownAlerts.current!.has(alert.id))
      .sort((left, right) => Date.parse(left.time) - Date.parse(right.time));
    const toDisplay = pending.slice(-3);
    for (const alert of pending) shownAlerts.current!.add(alert.id);
    for (const alert of toDisplay) {
      open?.({
        key: `runtime-alert-${alert.id}`,
        type: alert.severity === "error" ? "error" : "progress",
        message: alert.title.replaceAll("_", " "),
        description: alert.message,
      });
    }
    if (pending.length > 0) {
      try {
        window.sessionStorage.setItem(SHOWN_ALERTS_KEY, JSON.stringify([...shownAlerts.current!].slice(-500)));
      } catch { /* Browser storage is optional; in-memory de-duplication still works. */ }
    }
  }, [alerts, open]);

  useEffect(() => {
    const handleFailure = (event: Event) => {
      const failure = (event as CustomEvent<ApiRequestFailure>).detail;
      const key = `${failure.method}:${failure.path}:${failure.status}:${failure.message}`;
      const now = Date.now();
      if (now - (recentFailures.current.get(key) ?? 0) < 60_000) return;
      recentFailures.current.set(key, now);
      open?.({
        key: `api-error-${failure.status}-${now}`,
        type: "error",
        message: `API request failed (${failure.status})`,
        description: `${failure.message} · ${failure.path}`,
      });
    };
    window.addEventListener(API_REQUEST_FAILED_EVENT, handleFailure);
    return () => window.removeEventListener(API_REQUEST_FAILED_EVENT, handleFailure);
  }, [open]);
  return null;
}

function Sidebar({ collapsed, mobile, onNavigate }: { collapsed: boolean; mobile?: boolean; onNavigate?: () => void }) {
  return (
    <Flex direction="column" h="100%" bg="bg.panel" borderRightWidth={mobile ? "0" : "1px"} borderColor="border.default" w={collapsed ? "76px" : "248px"} transition="width .2s ease" overflow="hidden">
      <HStack h="64px" px={collapsed ? "3" : "4"} borderBottomWidth="1px" borderColor="border.subtle" gap="3" flexShrink="0">
        <Flex boxSize="36px" borderRadius="4px" bg="brand.500" color="white" align="center" justify="center"><CandlestickMark /></Flex>
        {!collapsed && <Box minW="0"><Text fontWeight="800" fontSize="sm" lineHeight="1.1">Trading OS</Text><Text color="fg.muted" fontSize="10px" mt="1">CONTROL CENTER</Text></Box>}
      </HStack>
      <Box flex="1" overflowY="auto" px="2" py="3">
        <Stack gap="4">
          {navGroups.map((group, groupIndex) => (
            <Stack key={`${group.label}-${groupIndex}`} gap="1">
              {!collapsed && group.label && <Text px="2.5" mb="1" color="fg.subtle" fontSize="10px" fontWeight="800" letterSpacing=".09em" textTransform="uppercase">{group.label}</Text>}
              {group.items.map((item) => (
                <NavLink key={item.to} to={item.to} onClick={onNavigate} style={{ textDecoration: "none" }}>
                  {({ isActive }) => (
                    <Flex h="38px" px={collapsed ? "0" : "2.5"} align="center" justify={collapsed ? "center" : "flex-start"} gap="2.5" borderRadius="4px" bg={isActive ? "rgba(99,102,241,.13)" : "transparent"} color={isActive ? "brand.400" : "fg.muted"} transition="background .14s ease, color .14s ease" _hover={{ bg: "bg.subtle", color: "fg.default" }}>
                      <Box as={item.icon} boxSize="4.5" flexShrink="0" />
                      {!collapsed && <><Text flex="1" fontSize="sm" fontWeight={isActive ? "700" : "550"}>{item.label}</Text>{item.badge && <Box minW="20px" px="1.5" h="20px" display="grid" placeItems="center" borderRadius="999px" bg="bg.subtle" color="fg.muted" fontSize="10px" fontWeight="800">{item.badge}</Box>}</>}
                    </Flex>
                  )}
                </NavLink>
              ))}
            </Stack>
          ))}
        </Stack>
      </Box>
      <Box p="3" borderTopWidth="1px" borderColor="border.subtle">
        <Flex p={collapsed ? "2" : "3"} bg="bg.subtle" borderRadius="4px" gap="2.5" align="center" justify={collapsed ? "center" : "flex-start"}>
          <Flex boxSize="28px" bg="rgba(16,185,129,.12)" color="status.positive" borderRadius="4px" align="center" justify="center"><Box as={Radio} boxSize="3.5" /></Flex>
          {!collapsed && <Box minW="0"><Text fontSize="xs" fontWeight="700">Runtime heartbeat polling</Text><Text fontSize="10px" color="fg.muted">2-second refresh</Text></Box>}
        </Flex>
      </Box>
    </Flex>
  );
}

function CandlestickMark() {
  return <Box w="16px" h="18px" position="relative"><Box position="absolute" left="3px" top="4px" w="4px" h="10px" bg="white" borderRadius="2px"/><Box position="absolute" left="4.5px" top="0" w="1px" h="18px" bg="white" opacity=".8"/><Box position="absolute" right="2px" top="7px" w="4px" h="7px" bg="white" opacity=".75" borderRadius="2px"/><Box position="absolute" right="3.5px" top="3px" w="1px" h="14px" bg="white" opacity=".65"/></Box>;
}

function Header({ onMenu }: { onMenu: () => void }) {
  const bots = useList<BotRecord>({ resource: "bots", pagination: { currentPage: 1, pageSize: 50 }, liveMode: "auto", queryOptions: { refetchInterval: 2_000 } }).result.data ?? [];
  const alertQuery = useCustom<AlertSummary>({ url: "/alerts/summary", method: "get", queryOptions: { refetchInterval: 5_000 } }).query;
  const alertSummary = alertQuery.data?.data;
  const unreadAlerts = alertSummary?.unread ?? 0;
  const active = bots.find((bot) => bot.status === "RUNNING") ?? bots[0];
  useEffect(() => {
    const refresh = () => { void alertQuery.refetch(); };
    window.addEventListener(ALERT_READ_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(ALERT_READ_CHANGED_EVENT, refresh);
  }, [alertQuery.refetch]);
  return (
    <Flex h="64px" px={{ base: "3", md: "5" }} bg="rgba(10,13,18,.72)" backdropFilter="blur(18px)" borderBottomWidth="1px" borderColor="border.default" align="center" gap="3" position="sticky" top="0" zIndex="100">
      <IconButton display={{ base: "inline-flex", lg: "none" }} aria-label="Open navigation" size="sm" variant="ghost" onClick={onMenu}><Box as={Menu} boxSize="5" /></IconButton>
      <HStack gap="2" minW="0"><ExecutionModePills bot={active} /><Box w="1px" h="24px" bg="border.default" display={{ base: "none", md: "block" }} /><Box display={{ base: "none", sm: "block" }}><Text fontSize="xs" fontWeight="700">{active ? `${active.broker} · ${active.symbol}` : "No active bot"}</Text><Text fontSize="10px" color="fg.muted">{active ? active.status : "No runtime"}</Text></Box></HStack>
      <Flex flex="1" />
      <HStack gap="1">
        <RuntimePulseDot bot={active} size="11px" />
        <ThemeToggle />
        <Box position="relative">
          <IconButton asChild aria-label={`Notifications${unreadAlerts > 0 ? `, ${unreadAlerts} unread` : ", no unread alerts"}`} size="sm" variant="ghost" color={unreadAlerts > 0 ? "fg.default" : "fg.muted"}>
            <Link to="/operations/alerts"><Box as={unreadAlerts > 0 ? BellRing : Bell} boxSize="4.5" /></Link>
          </IconButton>
          {unreadAlerts > 0 && <Flex aria-hidden="true" position="absolute" top="-3px" right="-4px" minW="18px" h="18px" px="1" borderRadius="999px" align="center" justify="center" bg={(alertSummary?.errorUnread ?? 0) > 0 ? "status.negative" : "status.warning"} color="white" borderWidth="2px" borderColor="bg.canvas" fontSize="8px" fontWeight="900" lineHeight="1" pointerEvents="none">{unreadAlerts > 99 ? "99+" : unreadAlerts}</Flex>}
        </Box>
        <Button asChild size="sm" variant="ghost" color="fg.default" px="2.5"><Link to="/settings" aria-label="Session settings"><HStack gap="2"><Flex boxSize="28px" align="center" justify="center" bg="brand.500" color="white" borderRadius="999px"><Box as={ShieldCheck} boxSize="3.5" /></Flex><Text display={{ base: "none", xl: "block" }} fontSize="xs">Session</Text></HStack></Link></Button>
      </HStack>
    </Flex>
  );
}

function MobileBottomNav() {
  const location = useLocation();
  const items = [
    { label: "Overview", to: "/overview", icon: navGroups[0].items[0].icon },
    { label: "Bots", to: "/bots", icon: navGroups[1].items[0].icon },
    { label: "Trade", to: "/trading/live", icon: navGroups[2].items[0].icon },
    { label: "Portfolio", to: "/portfolio", icon: navGroups[4].items[0].icon },
    { label: "More", to: "/operations/health", icon: navGroups[6].items[2].icon },
  ];
  return <Grid display={{ base: "grid", md: "none" }} position="fixed" bottom="0" left="0" right="0" zIndex="120" templateColumns="repeat(5, 1fr)" bg="bg.panel" borderTopWidth="1px" borderColor="border.default" minH="58px">{items.map((item) => { const active = location.pathname === item.to || location.pathname.startsWith(`${item.to}/`); return <Link key={item.to} to={item.to} style={{ textDecoration: "none" }}><Flex direction="column" align="center" justify="center" h="58px" color={active ? "brand.400" : "fg.muted"} gap="1"><Box as={item.icon} boxSize="4.5"/><Text fontSize="10px" fontWeight={active ? "800" : "600"}>{item.label}</Text></Flex></Link>;})}</Grid>;
}

export function AppLayout() {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  return (
    <Flex minH="100vh" bg="bg.canvas" color="fg.default">
      <RuntimeNotificationBridge />
      <Box display={{ base: "none", lg: "block" }} position="fixed" left="0" top="0" bottom="0" zIndex="110"><Sidebar collapsed={collapsed} /></Box>
      {mobileOpen && <Box display={{ base: "block", lg: "none" }} position="fixed" inset="0" zIndex="500" bg="rgba(3,6,10,.65)" backdropFilter="blur(6px)" onClick={() => setMobileOpen(false)}><Box w="280px" h="100%" onClick={(event) => event.stopPropagation()}><Sidebar collapsed={false} mobile onNavigate={() => setMobileOpen(false)} /></Box></Box>}
      <Box flex="1" ml={{ base: "0", lg: collapsed ? "76px" : "248px" }} transition="margin-left .2s ease" minW="0">
        <Header onMenu={() => setMobileOpen(true)} />
        <Flex display={{ base: "none", lg: "flex" }} position="fixed" left={collapsed ? "55px" : "227px"} top="78px" zIndex="140" transition="left .2s ease"><IconButton aria-label={collapsed ? "Expand sidebar" : "Collapse sidebar"} size="xs" borderRadius="999px" bg="bg.panel" borderWidth="1px" borderColor="border.default" color="fg.muted" onClick={() => setCollapsed((x) => !x)}><Box as={Menu} boxSize="3.5" /></IconButton></Flex>
        <Box maxW="1880px" mx="auto" px={{ base: "3", sm: "4", lg: "6" }} pt={{ base: "4", lg: "6" }} pb={{ base: "80px", md: "8" }}><Outlet /></Box>
      </Box>
      <MobileBottomNav />
    </Flex>
  );
}
